// iter270 · posthog-js is now loaded via the CDN snippet in index.html
// (its stub queues every call until array.js arrives). Importing the npm
// package here was bundling a SECOND full copy of PostHog (~70KB gz)
// into main.js and slowing every first paint. All helpers below proxy
// window.posthog instead.

import { isNativeApp } from './nativeBridge';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const CONSENT_KEY = 'flyt_cookie_consent';

const ph = () => (typeof window !== 'undefined' ? window.posthog : null);

let initialized = false;
let consentWaiterAttached = false;

/** Cookie-consent choice: 'accepted' | 'declined' | null. */
const _consent = () => {
  try { return window.localStorage.getItem(CONSENT_KEY); } catch { return null; }
};

/**
 * Boot PostHog once, off the critical path. The CDN stub only starts
 * downloading array.js when `init` is called — deferring to idle keeps
 * ~80KB of analytics JS from fighting the LCP on mobile.
 */
export const initAnalytics = () => {
  if (initialized || !KEY || typeof window === 'undefined') return;
  // iter672 · Consent gate (GDPR/ePrivacy) — PostHog is non-essential.
  //  • Web: only boot AFTER the user explicitly accepts cookies. If no
  //    choice yet, wait and boot on the `cookieconsent-change` accept
  //    event. A 'declined' choice means PostHog never loads at all.
  //  • Native app: the web cookie banner isn't shown (Apple guidance)
  //    and there's no cross-site cookie regime — analytics is governed
  //    by the App Store privacy labels, so we boot normally.
  if (!isNativeApp() && _consent() !== 'accepted') {
    if (!consentWaiterAttached) {
      consentWaiterAttached = true;
      window.addEventListener('cookieconsent-change', (e) => {
        if (e?.detail?.value === 'accepted') initAnalytics();
      });
    }
    return;
  }
  initialized = true;
  const boot = () => {
    const p = ph();
    if (!p || !p.init) return;
    p.init(KEY, {
      api_host: HOST,
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      persistence: 'localStorage',
      ip: false,                     // CCPA-safe by default
      session_recording: {
        recordCrossOriginIframes: true,
        capturePerformance: false,
      },
    });
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(boot, { timeout: 4000 });
  } else {
    setTimeout(boot, 2500);
  }
};

/** Capture a $pageview every time the SPA route changes. */
export const trackPageView = (path) => {
  if (!initialized || !KEY) return;
  ph()?.capture?.('$pageview', {
    $current_url: typeof window !== 'undefined' ? window.location.href : path,
    $pathname: path,
  });
};

/** Tie subsequent events to a stable user id after sign-in. */
export const identify = (userId, traits = {}) => {
  if (!initialized || !KEY || !userId) return;
  ph()?.identify?.(String(userId), traits);
};

/** Wipe identity on sign-out so the next visitor isn't merged into this person. */
export const resetIdentity = () => {
  if (!initialized || !KEY) return;
  ph()?.reset?.();
};

/** Generic event helper. Use the named exports below for the standard schema. */
export const track = (event, props = {}) => {
  if (!initialized || !KEY) return;
  try { ph()?.capture?.(event, props); } catch (_) { /* analytics never breaks UX */ }
};

// ─── Named events (keeps the schema consistent across the codebase) ─────────
export const trackStoryView = (story) => track('story_viewed', {
  story_id: story?.id, story_slug: story?.slug, story_title: story?.name,
  sport: story?.sport, channel: story?.channel, category: story?.category,
  athlete_id: story?.athlete_id, athlete_name: story?.athlete_name,
});

export const trackStoryCompleted = (story, durationSec) => track('story_completed', {
  story_id: story?.id, story_slug: story?.slug, story_title: story?.name,
  read_time_seconds: durationSec,
});

export const trackStorySaved = (story) => track('story_saved', {
  story_id: story?.id, story_slug: story?.slug, story_title: story?.name,
});

export const trackStoryShared = (story, channel) => track('story_shared', {
  story_id: story?.id, story_slug: story?.slug, share_channel: channel,
});

export const trackAthleteView = (a) => track('athlete_viewed', {
  athlete_id: a?.id, athlete_slug: a?.slug, athlete_name: a?.name, sport: a?.sport,
});

export const trackBrandClicked = (brand, where) => track('brand_clicked', {
  brand_id: brand?.id, brand_name: brand?.name, placement: where,
});

export const trackSignup = (method, traits = {}) => track('signup', { method, ...traits });
export const trackNewsletterSubscribed = (source) => track('newsletter_subscribed', { source });
export const trackPwaInstalled = () => track('pwa_installed');
export const trackBrandInquirySubmitted = (tier) => track('brand_inquiry_submitted', { tier });
export const trackSchoolInquirySubmitted = (tier) => track('school_inquiry_submitted', { tier });
export const trackAthleteApplicationSubmitted = (sport) => track('athlete_application_submitted', { sport });
